// src/http.js — Minimal fetch-based HTTP client (replaces axios)
import {
  getAccessToken,
  hasStoredAuthTokens,
  ensureValidAccessToken,
  recoverFromUnauthorized,
  redirectToAuthorize
} from './auth'

/** Never resolves — used while a full-page auth redirect is in progress. */
function waitForAuthRedirect() {
  return new Promise(function () {})
}

function buildAuthHeaders(headers, token) {
  if (token) {
    headers['Authorization'] = 'Bearer ' + token
  } else {
    delete headers['Authorization']
  }
  return headers
}

function createHttpError(status, data, message) {
  const err = new Error(message || (data && data.message) || 'HTTP ' + status)
  err.response = { status: status, data: data || {} }
  return err
}

function createNetworkError(cause) {
  const err = new Error((cause && cause.message) || 'Network request failed')
  err.response = undefined
  err.cause = cause
  return err
}

export function createHttpClient(baseURL) {
  /**
   * Core request function. Returns { data } on success, throws on error.
   * The thrown error has shape: { response: { status, data: { message } } }
   */
  async function request(method, url, body, config, isAuthRetry) {
    config = config || {}
    isAuthRetry = !!isAuthRetry
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
    if (config.headers) {
      Object.keys(config.headers).forEach(function (key) {
        headers[key] = config.headers[key]
      })
    }
    if (!isAuthRetry && hasStoredAuthTokens()) {
      const hasValidToken = await ensureValidAccessToken()
      if (!hasValidToken) {
        return waitForAuthRedirect()
      }
    }

    const token = getAccessToken()
    buildAuthHeaders(headers, token)

    let res
    try {
      res = await fetch(baseURL + url, {
        method: method,
        headers: headers,
        body: body ? JSON.stringify(body) : undefined
      })
    } catch (fetchError) {
      if (!isAuthRetry && token) {
        const recovered = await recoverFromUnauthorized(true)
        if (!recovered) {
          return waitForAuthRedirect()
        }
        return request(method, url, body, config, true)
      }
      throw createNetworkError(fetchError)
    }

    if (res.status === 401 && !isAuthRetry) {
      const recovered = await recoverFromUnauthorized(!!token)
      if (!recovered) {
        return waitForAuthRedirect()
      }
      return request(method, url, body, config, true)
    }

    let data
    try {
      data = await res.json()
    } catch (e) {
      data = null
    }

    if (res.status === 401) {
      await redirectToAuthorize()
      return waitForAuthRedirect()
    }

    if (!res.ok) {
      throw createHttpError(res.status, data)
    }

    return { data: data }
  }

  return {
    get: function (url, config) {
      return request('GET', url, null, config)
    },
    post: function (url, body, config) {
      return request('POST', url, body, config)
    },
    delete: function (url, config) {
      return request('DELETE', url, null, config)
    }
  }
}



// WEBPACK FOOTER //
// ./src/http.js